'use client';

import { type ComponentProps, type ReactNode } from 'react';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import { FOOTER_PAYMENT_METHOD_REGION_ID } from '@/features/global-footer/constants';
import { PAYMENT_METHOD_IDS } from '@/extensions/snk/lib/payment-method-platforms';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { cn } from '@/lib/utils';
import { LogoStack } from '@/shared-ui';
import { PaymentMethodIcon } from '../payment-method-icon';

export interface PaymentMethodIconListProps extends Omit<ComponentProps<'div'>, 'children'> {
    heading: string;
    icons?: ReactNode;
}

export const PaymentMethodIconList = ({ heading, icons, className, ...props }: PaymentMethodIconListProps) => {
    const trimmedHeading = heading.trim();
    if (!trimmedHeading) {
        return null;
    }

    return (
        <LogoStack.Root className={cn(className)} data-slot="payment-method-icon-list" {...props}>
            <LogoStack.Title className="text-primary-foreground">{trimmedHeading}</LogoStack.Title>
            <LogoStack.Wrapper>
                <div data-slot="payment-method-icon-row" className="flex flex-row flex-wrap gap-2">
                    {icons}
                </div>
            </LogoStack.Wrapper>
        </LogoStack.Root>
    );
};

function PaymentMethodsStatic({ className }: { className?: string }) {
    return (
        <PaymentMethodIconList
            className={className}
            heading="We accept"
            icons={PAYMENT_METHOD_IDS.map((method) => (
                <PaymentMethodIcon key={method} method={method} />
            ))}
        />
    );
}

type PaymentMethodsProps = {
    component?: Promise<ComponentWithComponentData | null> | ComponentWithComponentData | null;
    className?: string;
};

export const PaymentMethods = ({ component, className }: PaymentMethodsProps) => {
    if (component === undefined) {
        return <PaymentMethodsStatic className={className} />;
    }

    return (
        <div className={className}>
            <EmbeddedComponentRegion
                component={component}
                regionId={FOOTER_PAYMENT_METHOD_REGION_ID}
                fallbackElement={null}
                errorElement={null}
            />
        </div>
    );
};
